import { Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import * as _ from 'lodash';
import { isNumber } from '../../../models//utils';
import { ScrollablePopoverDirective } from '../../../directives/scrollable-popover/scrollable-popover.directive';
import { Endpoint, MessageType } from '../../../models/enums';
import { ExportDisabled, ExportExtraOptions, ExportOnChange, ExportStreamOptions, ExportValidation } from '../../../models/export-stream-options';
import { Timezone } from '../../../models/timezone';
import { RestService } from '../../../services/rest/rest.service';
import { CommonService } from '../../../services/common/common.service';
import { ActionType, ObjectActionListType, Permissions } from '../../../models/permission';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'res-export-output-formatting-options',
    templateUrl: './export-output-formatting-options.component.html',
    styleUrls: ['./export-output-formatting-options.component.scss']
})
export class ExportOutputFormattingOptionsComponent implements OnInit, OnChanges {
    readonly currentBrowserIsIE = this.commonService.currentBrowserIsIE;
    @ViewChildren(ScrollablePopoverDirective) errorPopovers: QueryList<ScrollablePopoverDirective>;
    @Input() automationId: string;

    @Input() exportOptions: ExportStreamOptions;
    @Input() isExportVolume: boolean;
    @Input() optionsDisabled: boolean;
    @Input() generateLoadFile: boolean;
    @Input() errorType: boolean;
    @Input() isReefReviewProject: boolean;

    @Input() extraOptions: ExportExtraOptions;
    @Input() onChange: ExportOnChange;
    @Input() disabled: ExportDisabled;
    @Input() validation: ExportValidation;
    @Input() isExportSettingsPage: boolean;
    @Input() isProjectSettings: boolean;
    @Input() isModal: boolean = false;
    private initialized: boolean = false;
    private isNewExportEvent: Subscription;
    private isNewExport: boolean = false;

    private _isBusy: boolean;
    get isBusy(): boolean {
        return this._isBusy;
    }
    @Input()
    set isBusy(val: boolean) {
        if (this._isBusy !== val) {
            this._isBusy = val;
            this.isBusyChange.emit(this._isBusy);
        }
    }
    @Output() isBusyChange = new EventEmitter<boolean>();

    private _isBusyMessage: string;
    get isBusyMessage(): string {
        return this._isBusyMessage;
    }
    @Input()
    set isBusyMessage(val: string) {
        if (this._isBusyMessage !== val) {
            this._isBusyMessage = val;
            this.isBusyMessageChange.emit(this._isBusyMessage);
        }
    }
    @Output() isBusyMessageChange = new EventEmitter<string>();

    isSystemUserWithPermissions = false;
    constructor(private commonService: CommonService, private restService: RestService) {
        this.isNewExportEvent = this.commonService.getMessage(MessageType.isNewExport).subscribe((data) => {
            this.isNewExport = true;
            this.initializeOnExportStreamLoaded();
        });
    }

    ngOnInit(): void {
        this.initializeOnExportStreamLoaded();
    }

    private initializeOnExportStreamLoaded() {
        if (!this.initialized && this.exportOptions && (this.exportOptions.timezone !== null || this.isNewExport)) {
            this.initialized = true;
            this.isSystemUserWithPermissions = (this.commonService.currentUser?.importedFromSystemUser || this.commonService.currentUser?.originalCustomerName === 'super_system_customer') && Permissions.checkUserPermission(ObjectActionListType.SYSTEM_EXPORT_FIELDS, ActionType.VIEW);
            this.loadTimezones();

            if (this.isReefReviewProject) {
                this.exportOptions.concordanceUnicodeFormat = true;
            }
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['exportOptions'] && changes['exportOptions'].currentValue) {
            this.initializeOnExportStreamLoaded();
        }
    }

    loadTimezones(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading...';

        this.extraOptions.isBusy.push(Endpoint.realmManager_listDRTimezones);
        this.restService.post(Endpoint.realmManager_listDRTimezones, {}).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.realmManager_listDRTimezones), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.timezones) {
                (response.timezones as Array<any>).map(responseItem => new Timezone(responseItem)).forEach((item) => {
                    if (this.extraOptions.timezones.length > 0 && this.extraOptions.timezones[0].id === item.id) {
                        this.extraOptions.timezones.splice(0, 1);
                    }
                    this.extraOptions.timezones.push(item);
                    if(this.isExportSettingsPage && this.exportOptions.timezone && this.exportOptions.timezone===item.description) {
                        this.exportOptions.timezone = item.id;
                        this.extraOptions.prevSelectedTimezone = item.id;
                    } else if (!this.isExportSettingsPage && !this.exportOptions.timezone && item.id === 'UTC' && !this.isReefReviewProject) {
                        this.exportOptions.timezone = item.id;
                        this.extraOptions.prevSelectedTimezone = item.id;
                    } else if (!this.isExportSettingsPage && item.id === 'US/Eastern' && this.isReefReviewProject) {
                        this.exportOptions.timezone = item.id;
                        this.extraOptions.prevSelectedTimezone = item.id;
                    }
                });
            }
            if (!this.extraOptions.prevSelectedTimezone && this.exportOptions.timezone) {
                this.extraOptions.prevSelectedTimezone = this.exportOptions.timezone;
            }
            const spacingOffsets = [-4.5, 3.5, 12.5];
            spacingOffsets.forEach(spacingOffset => {
                this.extraOptions.timezones.push(new Timezone({ description: '', id: '', offsetInfo: { hours: spacingOffset, mins: 0, negative: false } }));
            });

            this.extraOptions.timezones = this.extraOptions.timezones.sort((t1, t2): number => {
                const offset1 = t1.offsetInfo.negative ? -1 * t1.offsetInfo.hours : t1.offsetInfo.hours;
                const offset2 = t2.offsetInfo.negative ? -1 * t2.offsetInfo.hours : t2.offsetInfo.hours;
                if (offset1 > offset2) {
                    return 1;
                } else if (offset1 < offset2) {
                    return -1;
                }
                return 0;
            });

            this.extraOptions.timezones.push(new Timezone({
                description: 'Other...',
                id: 'Other'
            }));
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.realmManager_listDRTimezones), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });

        this.extraOptions.isBusy.push(Endpoint.realmManager_listAllTimezones);
        this.restService.post(Endpoint.realmManager_listAllTimezones, {}).then(response => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.realmManager_listAllTimezones), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
            if (response && response.timezones) {
                (response.timezones as Array<any>).map(responseItem => new Timezone(responseItem)).forEach((item) => {
                    this.extraOptions.allTimezones.push(item);
                });
            }

            const currentTimezone = this.extraOptions.allTimezones?.find(entry => entry?.id === this.exportOptions?.timezone);
            const isZoneAlreadyAdded = this.extraOptions.timezones?.find(entry => entry?.id === currentTimezone?.id);
            if (currentTimezone && (this.optionsDisabled || this.isExportVolume) && !isZoneAlreadyAdded) {
                this.extraOptions.timezones.push(currentTimezone);
            }
        }, error => {
            this.extraOptions.isBusy.splice(this.extraOptions.isBusy.indexOf(Endpoint.realmManager_listAllTimezones), 1);
            this.isBusy = this.extraOptions.isBusy.length > 0;
        });
    }

    restrictNumeric = (event): boolean => {
        if (event) {
            return (event.charCode === 8 || event.charCode === 0) ? false : event.charCode >= 48 && event.charCode <= 57;
        }
        return false;
    }

    parseNumeric = (modelName: string, min?: number, max?: number): void => {
        if (modelName) {
            const field = _.get(this, modelName);
            if (field) {
                let val = parseInt(field, 10);
                if (isNaN(val)) {
                    return;
                }
                if (isNumber(min) && min > val) {
                    val = min;
                }
                if (isNumber(max) && val > max) {
                    val = max;
                }
                _.set(this, modelName, val);
            }
        }
    }

    trimLeadingSpaces(type): void {
        if (type && this.exportOptions) {
            this.exportOptions[type] = this.exportOptions[type].trim();
        }

    }

    ngOnDestroy(): void {
        this.isNewExportEvent.unsubscribe();
    }
}
